import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import {
  ArrowLeft,
  Plus,
  Search,
  User,
  UserRoundMinus,
  ChevronDown,
  LogOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import { showToast } from '@/components/Toast';
import MobileHeader from '@/components/MobileHeader';
import { useAuthStore } from '@/stores';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

export type MemberRole = 'owner' | 'manager' | 'editor' | 'viewer';

interface ProjectMember {
  id: string;
  name: string;
  email: string;
  role: MemberRole;
  avatar?: string;
}

interface AvailableUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface ProjectMembersProps {
  projectId: string;
  projectName: string;
  projectArchived?: boolean; // 🔥 项目是否已归档
  onBack: () => void;
}

const ROLE_HIERARCHY: Record<MemberRole, number> = {
  owner: 4,
  manager: 3,
  editor: 2,
  viewer: 1,
};

const ROLE_COLORS: Record<MemberRole, string> = {
  owner: 'bg-purple-100 text-purple-700 border-purple-200',
  manager: 'bg-blue-100 text-blue-700 border-blue-200',
  editor: 'bg-green-100 text-green-700 border-green-200',
  viewer: 'bg-gray-100 text-gray-700 border-gray-200',
};

export default function ProjectMembers({
  projectId,
  projectName,
  projectArchived = false,
  onBack,
}: ProjectMembersProps) {
  const { t } = useTranslation();
  useForceUpdate();

  // Role labels using i18n
  const ROLE_LABELS: Record<MemberRole, string> = {
    owner: t('roles.owner'),
    manager: t('roles.manager'),
    editor: t('roles.editor'),
    viewer: t('roles.viewer'),
  };

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<MemberRole | null>(
    null
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AvailableUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<MemberRole>('viewer');
  const [isLoading, setIsLoading] = useState(false);
  const [searchMemberQuery, setSearchMemberQuery] = useState('');
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [updatingRoleMemberId, setUpdatingRoleMemberId] = useState<
    string | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    memberId: string;
    memberRole: MemberRole;
    memberName: string;
  } | null>(null);
  const [isQuitting, setIsQuitting] = useState(false);
  const [confirmQuit, setConfirmQuit] = useState(false);
  //   const { toast } = useToast();
  const { userInfo } = useAuthStore();

  // Reset state when projectId changes
  useEffect(() => {
    setMembers([]);
    setCurrentUserRole(null);
    setSearchMemberQuery('');
    setIsAddModalOpen(false);
  }, [projectId]);

  // Check if current user can edit members (editor and above can edit)
  // 🔥 archived 项目禁止编辑成员
  const canEdit =
    currentUserRole !== null &&
    ROLE_HIERARCHY[currentUserRole] > ROLE_HIERARCHY['viewer'] &&
    !projectArchived;

  // Check if current user can add members (only manager and owner can add)
  // 🔥 archived 项目禁止添加成员
  const canAddMembers =
    currentUserRole !== null &&
    ROLE_HIERARCHY[currentUserRole] > ROLE_HIERARCHY['editor'] &&
    !projectArchived;
  // Get available roles for adding (cannot add roles higher than current user)
  const getAvailableRoles = (): MemberRole[] => {
    if (!currentUserRole) return ['viewer'];
    const currentLevel = ROLE_HIERARCHY[currentUserRole];
    return (Object.keys(ROLE_HIERARCHY) as MemberRole[]).filter(
      role => ROLE_HIERARCHY[role] < currentLevel
    );
  };

  // Load project members
  const loadMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiGet<any>(`/api/projects/${projectId}/users`);
      const data = response?.data || response || {};

      if (data.users) {
        // Transform API response to match component interface
        const transformedMembers = data.users.map((item: any) => ({
          id: item.user.id.toString(),
          name: item.user.name,
          email: item.user.email,
          role: item.role,
        }));
        setMembers(transformedMembers);

        // Find current user's role by matching user ID
        const currentUserId = userInfo?.id?.toString();
        if (currentUserId) {
          const currentMember = transformedMembers.find(
            (m: any) => m.id === currentUserId
          );
          if (currentMember) {
            console.log('Found current user role:', currentMember.role);
            setCurrentUserRole(currentMember.role);
          } else {
            console.log(
              'Current user not found in members list, using owner role for testing'
            );
            setCurrentUserRole('owner'); // 临时给权限用于测试
          }
        } else {
          console.log('No userInfo.id found, using owner role for testing');
          setCurrentUserRole('owner'); // 临时给权限用于测试
        }
      }
    } catch (error) {
      console.error('Error loading members:', error);
      showToast(t('errors.failedToLoadProjectMembers'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, userInfo, t]);

  // Load available users for adding
  const loadAvailableUsers = useCallback(async () => {
    try {
      const response = await apiGet<any>('/api/shared-users');
      const data = response?.data || response || {};

      if (data.users) {
        // Filter out users who are already members (convert IDs to string for comparison)
        const memberIds = new Set(members.map(m => String(m.id)));
        const available = data.users.filter(
          (u: AvailableUser) => !memberIds.has(String(u.id))
        );
        setAvailableUsers(available);
        setFilteredUsers(available);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showToast(t('errors.failedToLoadUsers'), 'error');
    }
  }, [members, t]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (isAddModalOpen) {
      loadAvailableUsers();
    }
  }, [isAddModalOpen, loadAvailableUsers]);

  // Filter users based on search
  useEffect(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    if (trimmedQuery) {
      setFilteredUsers(
        availableUsers.filter(
          user =>
            user.name.toLowerCase().includes(trimmedQuery) ||
            user.email.toLowerCase().includes(trimmedQuery)
        )
      );
    } else {
      setFilteredUsers(availableUsers);
    }
  }, [searchQuery, availableUsers]);

  // Handle user selection
  const handleToggleUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleToggleAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  // 设置"全选" checkbox 的 indeterminate（半选）状态
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const isSomeSelected =
        selectedUsers.size > 0 && selectedUsers.size < filteredUsers.length;
      selectAllCheckboxRef.current.indeterminate = isSomeSelected;
    }
  }, [selectedUsers, filteredUsers]);

  // Add members
  const handleAddMembers = async () => {
    if (selectedUsers.size === 0) return;

    try {
      setIsLoading(true);
      const res = await apiPost(`/api/projects/${projectId}/users`, {
        users: Array.from(selectedUsers).map(userId => ({
          userId: parseInt(userId),
          role: selectedRole,
        })),
      });
      if (res.code === 200) {
        showToast(
          t('toast.successfullyAdded', { count: selectedUsers.size }),
          'success'
        );

        setIsAddModalOpen(false);
        setSelectedUsers(new Set());
        setSearchQuery('');
        setSelectedRole('viewer');
        loadMembers();
      }
    } catch (error) {
      console.error('Error adding members:', error);
      showToast(t('errors.failedToAddMembers'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove member - show confirmation
  const handleRemoveMemberClick = (
    memberId: string,
    memberRole: MemberRole,
    memberName: string
  ) => {
    if (!currentUserRole) return;

    // Check permission: can only remove members with lower role
    if (ROLE_HIERARCHY[memberRole] >= ROLE_HIERARCHY[currentUserRole]) {
      showToast(t('toast.onlyRemoveLowerRoles'), 'error');
      return;
    }

    setConfirmDelete({ memberId, memberRole, memberName });
  };

  // Confirm remove member
  const handleConfirmRemove = async () => {
    if (!confirmDelete) return;

    try {
      setRemovingMemberId(confirmDelete.memberId);
      const res = await apiDelete(`/api/projects/${projectId}/users`, {
        userIds: [parseInt(confirmDelete.memberId)],
      });
      if (res.code === 200) {
        showToast(t('toast.memberRemoved'), 'success');
        loadMembers();
      }
    } catch (error) {
      console.error('Error removing member:', error);
      showToast(t('errors.failedToRemoveMember'), 'error');
    } finally {
      setRemovingMemberId(null);
      setConfirmDelete(null);
    }
  };

  // Quit project (for non-owner members)
  const handleQuitProject = async () => {
    try {
      setIsQuitting(true);
      const res = await apiPost(`/api/projects/${projectId}/quit`, {});
      if (res.code === 200) {
        showToast(t('toast.quitProjectSuccess'), 'success');
        // 🔥 刷新侧边栏项目列表
        if (window.refreshSidebarProjects) {
          window.refreshSidebarProjects();
        }
        // 🔥 刷新侧边栏 Starred 列表（包含 starred 的项目和对话）
        // 需要 await 确保数据更新后再跳转页面
        if (window.refreshSidebarStarred) {
          await window.refreshSidebarStarred();
        }
        // Navigate to #/chat
        window.location.hash = '#/chat';
      }
    } catch (error) {
      console.error('Error quitting project:', error);
      showToast(t('errors.failedToQuitProject'), 'error');
    } finally {
      setIsQuitting(false);
      setConfirmQuit(false);
    }
  };

  // Change member role
  const handleChangeRole = async (
    memberId: string,
    newRole: MemberRole,
    currentRole: MemberRole
  ) => {
    if (!currentUserRole) return;

    // Check permission: new role must be lower than current user's role
    if (ROLE_HIERARCHY[newRole] >= ROLE_HIERARCHY[currentUserRole]) {
      showToast(t('toast.cannotAssignHigherRole'), 'error');
      return;
    }

    // Check permission: can only change roles for members with lower role
    if (ROLE_HIERARCHY[currentRole] >= ROLE_HIERARCHY[currentUserRole]) {
      showToast(t('toast.onlyChangeLowerRoles'), 'error');
      return;
    }

    try {
      setUpdatingRoleMemberId(memberId);
      const res = await apiPost(`/api/projects/${projectId}/users`, {
        users: [
          {
            userId: parseInt(memberId),
            role: newRole,
          },
        ],
      });
      if (res.code === 200) {
        showToast(t('toast.roleUpdated'), 'success');
        loadMembers();
      }
    } catch (error) {
      console.error('Error updating member role:', error);
      showToast(t('errors.failedToUpdateRole'), 'error');
    } finally {
      setUpdatingRoleMemberId(null);
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get avatar color based on name hash
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-pink-500',
      'bg-purple-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-orange-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Filter displayed members
  const trimmedMemberQuery = searchMemberQuery.trim().toLowerCase();
  const filteredMembers = members.filter(
    member =>
      member.name.toLowerCase().includes(trimmedMemberQuery) ||
      member.email.toLowerCase().includes(trimmedMemberQuery)
  );

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Mobile Header */}
      <MobileHeader
        rightActions={
          <button
            onClick={onBack}
            className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('tooltips.backToProjects')}
          >
            <ArrowLeft size={18} />
          </button>
        }
      >
        <h3 className="text-base font-bold truncate">
          {t('project.memberManagement')}
        </h3>
      </MobileHeader>
      {/* Desktop Header with max-width container */}
      <div className="flex-shrink-0 w-full hidden lg:block">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-12 pb-4 sm:pb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('tooltips.backToProjects')}
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <h1 className="text-xl sm:text-3xl font-normal text-gray-900 font-serif">
              {t('project.memberManagement')}
            </h1>
          </div>
          <div className="text-sm text-gray-600 ml-12 break-words">
            {projectName}
          </div>
        </div>
      </div>

      {/* Search and Add Button with max-width container */}
      <div className="flex-shrink-0 w-full">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder={t('project.searchMembersByNameOrEmail')}
                value={searchMemberQuery}
                onChange={e => setSearchMemberQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 bg-white text-sm"
              />
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {canAddMembers && (
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  disabled={projectArchived}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <Plus size={16} />
                  <span>{t('project.addMembers')}</span>
                </button>
              )}
              {/* Quit Project button - only visible for non-owner members, disabled when archived */}
              {currentUserRole && currentUserRole !== 'owner' && (
                <button
                  onClick={() => setConfirmQuit(true)}
                  disabled={isQuitting || projectArchived}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                >
                  <LogOut size={16} />
                  <span>{t('project.quitProject')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Members Table with max-width container */}
      <div className="flex-1 overflow-y-auto w-full">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pb-12">
          <div className="bg-white rounded-xl border border-gray-300 overflow-hidden mt-4 sm:mt-6">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px]">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.member')}
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.role')}
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-3 sm:px-6 py-8 text-center text-gray-500"
                      >
                        {t('project.loadingMembers')}
                      </td>
                    </tr>
                  ) : filteredMembers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-3 sm:px-6 py-8 text-center text-gray-500"
                      >
                        {t('project.noMembersFound')}
                      </td>
                    </tr>
                  ) : (
                    filteredMembers.map(member => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-600 flex items-center justify-center text-white font-semibold text-xs sm:text-sm flex-shrink-0">
                              {getUserInitials(member.name)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {member.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {member.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className={cn(
                                  'inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors',
                                  ROLE_COLORS[member.role],
                                  canEdit &&
                                    currentUserRole &&
                                    ROLE_HIERARCHY[member.role] <
                                      ROLE_HIERARCHY[currentUserRole]
                                    ? 'cursor-pointer hover:opacity-80'
                                    : 'cursor-default',
                                  updatingRoleMemberId === member.id &&
                                    'opacity-50 pointer-events-none'
                                )}
                                disabled={
                                  !canEdit ||
                                  !currentUserRole ||
                                  updatingRoleMemberId === member.id ||
                                  ROLE_HIERARCHY[member.role] >=
                                    ROLE_HIERARCHY[currentUserRole]
                                }
                              >
                                {updatingRoleMemberId === member.id ? (
                                  <>
                                    <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full mr-1"></span>
                                    {ROLE_LABELS[member.role]}
                                  </>
                                ) : (
                                  <>
                                    {ROLE_LABELS[member.role]}
                                    {canEdit &&
                                      currentUserRole &&
                                      ROLE_HIERARCHY[member.role] <
                                        ROLE_HIERARCHY[currentUserRole] && (
                                        <ChevronDown size={12} />
                                      )}
                                  </>
                                )}
                              </button>
                            </DropdownMenuTrigger>
                            {canEdit &&
                              currentUserRole &&
                              ROLE_HIERARCHY[member.role] <
                                ROLE_HIERARCHY[currentUserRole] && (
                                <DropdownMenuContent align="start">
                                  {(Object.keys(ROLE_HIERARCHY) as MemberRole[])
                                    .filter(
                                      role =>
                                        ROLE_HIERARCHY[role] <
                                        ROLE_HIERARCHY[currentUserRole]
                                    )
                                    .map(role => (
                                      <DropdownMenuItem
                                        key={role}
                                        onClick={() =>
                                          handleChangeRole(
                                            member.id,
                                            role,
                                            member.role
                                          )
                                        }
                                        className={cn(
                                          'cursor-pointer',
                                          role === member.role && 'bg-gray-100'
                                        )}
                                      >
                                        <span
                                          className={cn(
                                            'text-xs',
                                            role === member.role &&
                                              'font-medium'
                                          )}
                                        >
                                          {ROLE_LABELS[role]}
                                        </span>
                                      </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                              )}
                          </DropdownMenu>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 text-right">
                          {canEdit &&
                            currentUserRole &&
                            ROLE_HIERARCHY[member.role] <
                              ROLE_HIERARCHY[currentUserRole] && (
                              <button
                                onClick={() =>
                                  handleRemoveMemberClick(
                                    member.id,
                                    member.role,
                                    member.name
                                  )
                                }
                                disabled={removingMemberId === member.id}
                                className={cn(
                                  'p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors',
                                  removingMemberId === member.id &&
                                    'opacity-50 pointer-events-none'
                                )}
                                title={t('tooltips.removeMember')}
                              >
                                {removingMemberId === member.id ? (
                                  <span className="animate-spin inline-block w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full"></span>
                                ) : (
                                  <UserRoundMinus size={16} />
                                )}
                              </button>
                            )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Members Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('project.addMembers')}</DialogTitle>
            <div className="text-sm text-gray-600 mt-1">{projectName}</div>
          </DialogHeader>

          <div className="px-6 py-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder={t('form.searchMembers')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* User List */}
            <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
              {/* Select All */}
              <div
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200"
                onClick={handleToggleAll}
              >
                <User size={20} className="text-gray-600" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {t('share.all')}
                  </div>
                </div>
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  checked={
                    selectedUsers.size === filteredUsers.length &&
                    filteredUsers.length > 0
                  }
                  onChange={handleToggleAll}
                  className="w-4 h-4 accent-gray-800 rounded border-gray-300 focus:ring-gray-500"
                />
              </div>

              {/* Individual Users */}
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleToggleUser(user.id)}
                >
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs',
                      getAvatarColor(user.name)
                    )}
                  >
                    {getUserInitials(user.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {user.email}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedUsers.has(user.id)}
                    onChange={() => handleToggleUser(user.id)}
                    className="w-4 h-4 accent-gray-800 rounded border-gray-300 focus:ring-gray-500"
                  />
                </div>
              ))}

              {filteredUsers.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  {searchQuery
                    ? t('project.noUsersFound')
                    : t('project.noAvailableUsers')}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">
                  {t('project.addAs')}
                </label>
                <select
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value as MemberRole)}
                  className="px-2 py-1 text-xs bg-white text-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-gray-300 cursor-pointer"
                >
                  {getAvailableRoles().map(role => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">
                  {t('project.selectedCount', { count: selectedUsers.size })}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setIsAddModalOpen(false)}
                  className="h-8 px-3 text-sm border-gray-300 hover:bg-gray-100"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleAddMembers}
                  disabled={
                    selectedUsers.size === 0 || isLoading || projectArchived
                  }
                  className="h-8 px-3 text-sm bg-gray-900 hover:bg-gray-800 text-white disabled:bg-gray-400"
                >
                  {isLoading ? t('project.adding') : t('project.add')}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={() => setConfirmDelete(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('project.removeMember')}</DialogTitle>
          </DialogHeader>
          <div className="py-4 px-4">
            <p className="text-sm text-gray-600">
              {t('project.removeMemberConfirm', {
                name: confirmDelete?.memberName,
              })}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={!!removingMemberId}
              className="h-9 px-4 border-gray-300 hover:bg-gray-100"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmRemove}
              disabled={!!removingMemberId}
              className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white disabled:bg-red-400"
            >
              {removingMemberId ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  {t('project.removing')}
                </>
              ) : (
                t('project.remove')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quit Project Confirmation Modal */}
      <Dialog open={confirmQuit} onOpenChange={setConfirmQuit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('project.quitProject')}</DialogTitle>
          </DialogHeader>
          <div className="py-4 px-4">
            <p className="text-sm text-gray-600">
              {t('project.quitProjectConfirm', { name: projectName })}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmQuit(false)}
              disabled={isQuitting}
              className="h-9 px-4 border-gray-300 hover:bg-gray-100"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleQuitProject}
              disabled={isQuitting || projectArchived}
              className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white disabled:bg-red-400"
            >
              {isQuitting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                  {t('project.quitting')}
                </>
              ) : (
                t('project.quit')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
